import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Trash2, CreditCard as Edit2, ChevronDown, AlertCircle, Book, Plus, Pencil, Settings, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface BoardPost {
  id: string;
  author_id: string;
  city: string;
  content: string;
  created_at: string;
  updated_at: string;
  author?: {
    id: string;
    name: string;
  };
}

interface Profile {
  id: string;
  name: string;
  role: string;
  city: string | null;
}

interface VademecumItem {
  id: string;
  title: string;
  content: string;
  category_id: string;
  created_at: string;
}

interface VademecumCategory {
  id: string;
  name: string;
  order: number;
}

type ActiveTab = { type: 'board'; city: string } | { type: 'vademecum'; categoryId: string };

const cities = [
  { value: 'ITALIA', label: 'Italia' },
  { value: 'FIRENZE', label: 'Firenze' },
  { value: 'ROMA', label: 'Roma' },
  { value: 'TORINO', label: 'Torino' },
  { value: 'MILANO', label: 'Milano' },
  { value: 'BOLOGNA', label: 'Bologna' },
  { value: 'NAPOLI', label: 'Napoli' },
  { value: 'PALERMO', label: 'Palermo' },
  { value: 'BARI', label: 'Bari' },
  { value: 'GENOVA', label: 'Genova' },
  { value: 'VENEZIA', label: 'Venezia' }
];

const Board = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [editingPost, setEditingPost] = useState<BoardPost | null>(null);
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [availableCities, setAvailableCities] = useState<typeof cities>([]);

  const [activeTab, setActiveTab] = useState<ActiveTab>({ type: 'board', city: 'ITALIA' });

  const [vademecumCategories, setVademecumCategories] = useState<VademecumCategory[]>([]);
  const [vademecumItems, setVademecumItems] = useState<VademecumItem[]>([]);
  const [vademecumLoading, setVademecumLoading] = useState(false);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isVademecumModalOpen, setIsVademecumModalOpen] = useState(false);
  const [editingVademecumItem, setEditingVademecumItem] = useState<VademecumItem | undefined>(undefined);

  const selectedCity = activeTab.type === 'board' ? activeTab.city : null;

  useEffect(() => {
    loadUserProfile();
    loadVademecumCategories();
  }, [user]);

  useEffect(() => {
    if (userProfile && selectedCity) {
      loadPosts();
    }
  }, [selectedCity, userProfile]);

  useEffect(() => {
    if (activeTab.type === 'vademecum') {
      loadVademecumItems(activeTab.categoryId);
    }
  }, [activeTab]);


  const loadUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, role, city')
        .eq('id', user?.id)
        .single();

      if (error) throw error;

      setUserProfile(data);

      let available: typeof cities = [];

      if (data.role === 'admin') {
        available = cities;
      } else if (data.role === 'organizer' || data.role === 'pr') {
        available = [{ value: 'ITALIA', label: 'Italia' }];
        if (data.city) {
          const cityOption = cities.find(c => c.value === data.city);
          if (cityOption) {
            available.push(cityOption);
          }
        }
      }

      setAvailableCities(available);

      if (available.length > 0) {
        setActiveTab({ type: 'board', city: available[0].value });
      }
    } catch (err: any) {
      console.error('Error loading user profile:', err);
      setError(err.message);
    }
  };

  const loadVademecumCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('vademecum_categories')
        .select('*')
        .order('order');

      if (error) throw error;
      setVademecumCategories(data || []);
    } catch (err: any) {
      console.error('Error loading vademecum categories:', err);
    }
  };

  const loadVademecumItems = async (categoryId: string) => {
    try {
      setVademecumLoading(true);
      const { data, error } = await supabase
        .from('vademecum')
        .select('id, title, content, category_id, created_at')
        .eq('category_id', categoryId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVademecumItems(data || []);
    } catch (err: any) {
      console.error('Error loading vademecum items:', err);
      setError(err.message);
    } finally {
      setVademecumLoading(false);
    }
  };

  const loadPosts = async () => {
    if (!selectedCity) return;
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('board_posts')
        .select(`
          *,
          author:profiles!board_posts_author_id_fkey (
            id,
            name
          )
        `)
        .eq('city', selectedCity)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (err: any) {
      console.error('Error loading posts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPostContent.trim() || !selectedCity) return;

    try {
      setSubmitting(true);
      setError(null);

      const { error } = await supabase
        .from('board_posts')
        .insert({
          author_id: user?.id,
          city: selectedCity,
          content: newPostContent.trim()
        });

      if (error) throw error;

      setNewPostContent('');
      loadPosts();
    } catch (err: any) {
      console.error('Error creating post:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPost = (post: BoardPost) => {
    setEditingPost(post);
    setEditContent(post.content);
  };

  const handleUpdatePost = async () => {
    if (!editingPost || !editContent.trim()) return;

    try {
      setSubmitting(true);
      setError(null);

      const { error } = await supabase
        .from('board_posts')
        .update({ content: editContent.trim() })
        .eq('id', editingPost.id);

      if (error) throw error;

      setEditingPost(null);
      setEditContent('');
      loadPosts();
    } catch (err: any) {
      console.error('Error updating post:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo post?')) return;

    try {
      setError(null);
      const { error } = await supabase
        .from('board_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      loadPosts();
    } catch (err: any) {
      console.error('Error deleting post:', err);
      setError(err.message);
    }
  };

  const handleVademecumSubmit = async (formData: { title: string; content: string; category_id: string }) => {
    try {
      setError(null);

      if (editingVademecumItem) {
        const { error } = await supabase
          .from('vademecum')
          .update({
            title: formData.title,
            content: formData.content,
            category_id: formData.category_id
          })
          .eq('id', editingVademecumItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('vademecum')
          .insert([{
            title: formData.title,
            content: formData.content,
            category_id: formData.category_id
          }]);
        if (error) throw error;
      }

      setIsVademecumModalOpen(false);
      setEditingVademecumItem(undefined);
      if (activeTab.type === 'vademecum') {
        loadVademecumItems(activeTab.categoryId);
      }
    } catch (err: any) {
      console.error('Error saving vademecum:', err);
      setError(err.message);
    }
  };

  const handleDeleteVademecumItem = async (id: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa voce?')) return;

    try {
      setError(null);
      const { error } = await supabase
        .from('vademecum')
        .delete()
        .eq('id', id);

      if (error) throw error;

      if (activeTab.type === 'vademecum') {
        loadVademecumItems(activeTab.categoryId);
      }
    } catch (err: any) {
      console.error('Error deleting vademecum item:', err);
      setError(err.message);
    }
  };

  const handleSaveCategories = async (updatedCategories: VademecumCategory[]) => {
    try {
      setError(null);

      const oldCategories = new Map(vademecumCategories.map(cat => [cat.name, cat.id]));

      const categoriesToUpsert = updatedCategories.map((cat, index) => ({
        id: oldCategories.get(cat.name) || cat.id,
        name: cat.name,
        order: index + 1
      }));

      const categoriesToDelete = vademecumCategories
        .filter(oldCat => !updatedCategories.some(newCat => newCat.name === oldCat.name))
        .map(cat => cat.id);

      const { error } = await supabase.rpc('manage_vademecum_categories', {
        categories_to_upsert: categoriesToUpsert,
        categories_to_delete: categoriesToDelete
      });

      if (error) throw error;

      await loadVademecumCategories();
      setIsCategoryModalOpen(false);

      if (activeTab.type === 'vademecum') {
        const stillExists = updatedCategories.some(c =>
          (oldCategories.get(c.name) || c.id) === activeTab.categoryId
        );
        if (!stillExists && updatedCategories.length > 0) {
          setActiveTab({ type: 'vademecum', categoryId: updatedCategories[0].id });
        } else if (updatedCategories.length === 0) {
          setActiveTab({ type: 'board', city: availableCities[0]?.value || 'ITALIA' });
        }
      }
    } catch (err: any) {
      console.error('Error saving categories:', err);
      setError(err.message);
    }
  };

  const canPostInCity = () => {
    if (!userProfile || !selectedCity) return false;
    if (userProfile.role === 'admin') return true;
    if (userProfile.role === 'organizer') {
      return selectedCity === 'ITALIA' || selectedCity === userProfile.city;
    }
    if (userProfile.role === 'pr') {
      return selectedCity === userProfile.city;
    }
    return false;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minuto' : 'minuti'} fa`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'ora' : 'ore'} fa`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'giorno' : 'giorni'} fa`;

    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const isAdmin = userProfile?.role === 'admin';

  if (!userProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (availableCities.length === 0) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900 mb-2">Citta non assegnata</h3>
              <p className="text-yellow-800">
                Per accedere alla bacheca, devi avere una citta assegnata.
                Contatta l'amministratore per richiedere l'assegnazione.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-rose-700 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <MessageSquare className="w-8 h-8 text-white" />
                Bacheca
              </h1>
              <p className="text-white/80 mt-1">Condividi pensieri, progetti e comunicazioni con il team</p>
            </div>
            {isAdmin && activeTab.type === 'vademecum' && (
              <div className="flex gap-2">
                <button
                  onClick={() => setIsCategoryModalOpen(true)}
                  className="bg-white/20 backdrop-blur-sm border border-white/30 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-all flex items-center gap-2 text-sm font-medium"
                >
                  <Settings className="h-4 w-4" />
                  Gestisci Categorie
                </button>
                <button
                  onClick={() => {
                    setEditingVademecumItem(undefined);
                    setIsVademecumModalOpen(true);
                  }}
                  className="bg-white text-rose-600 px-4 py-2 rounded-lg hover:bg-rose-50 transition-all flex items-center gap-2 text-sm font-medium"
                >
                  <Plus className="h-4 w-4" />
                  Nuova Voce
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">
                    {activeTab.type === 'board' ? 'Post Totali' : 'Voci'}
                  </p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {activeTab.type === 'board' ? posts.length : vademecumItems.length}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  {activeTab.type === 'board'
                    ? <MessageSquare className="h-6 w-6 text-white" />
                    : <Book className="h-6 w-6 text-white" />
                  }
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Sezione Attiva</p>
                  <p className="text-xl font-bold text-white mt-1">
                    {activeTab.type === 'board'
                      ? availableCities.find(c => c.value === activeTab.city)?.label || ''
                      : vademecumCategories.find(c => c.id === activeTab.categoryId)?.name || ''
                    }
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Bacheche</p>
                  <p className="text-3xl font-bold text-white mt-1">{availableCities.length}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-6">
          {isAdmin ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative inline-block w-64">
                <select
                  value={activeTab.type === 'board' ? activeTab.city : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setActiveTab({ type: 'board', city: e.target.value });
                    }
                  }}
                  className={`w-full appearance-none border rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent cursor-pointer text-sm font-medium ${
                    activeTab.type === 'board'
                      ? 'bg-white text-gray-900 border-gray-300'
                      : 'bg-gray-50 text-gray-500 border-gray-200'
                  }`}
                >
                  {availableCities.map(city => (
                    <option key={city.value} value={city.value}>
                      Bacheca {city.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {vademecumCategories.length > 0 && (
                <div className="h-8 w-px bg-gray-300" />
              )}

              {vademecumCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab({ type: 'vademecum', categoryId: cat.id })}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    activeTab.type === 'vademecum' && activeTab.categoryId === cat.id
                      ? 'bg-rose-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg inline-flex">
              {availableCities.map(city => (
                <button
                  key={city.value}
                  onClick={() => setActiveTab({ type: 'board', city: city.value })}
                  className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                    activeTab.type === 'board' && activeTab.city === city.value
                      ? 'bg-white text-rose-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {city.label}
                </button>
              ))}

              {vademecumCategories.length > 0 && (
                <div className="h-8 w-px bg-gray-300 self-center mx-1" />
              )}

              {vademecumCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab({ type: 'vademecum', categoryId: cat.id })}
                  className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                    activeTab.type === 'vademecum' && activeTab.categoryId === cat.id
                      ? 'bg-white text-rose-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {activeTab.type === 'board' ? (
          <BoardContent
            posts={posts}
            loading={loading}
            submitting={submitting}
            newPostContent={newPostContent}
            setNewPostContent={setNewPostContent}
            editingPost={editingPost}
            editContent={editContent}
            setEditContent={setEditContent}
            canPost={canPostInCity()}
            selectedCityLabel={availableCities.find(c => c.value === selectedCity)?.label || ''}
            userId={user?.id}
            onSubmitPost={handleSubmitPost}
            onEditPost={handleEditPost}
            onUpdatePost={handleUpdatePost}
            onDeletePost={handleDeletePost}
            onCancelEdit={() => { setEditingPost(null); setEditContent(''); }}
            formatDate={formatDate}
          />
        ) : (
          <VademecumContent
            items={vademecumItems}
            loading={vademecumLoading}
            isAdmin={isAdmin}
            onEdit={(item) => { setEditingVademecumItem(item); setIsVademecumModalOpen(true); }}
            onDelete={handleDeleteVademecumItem}
          />
        )}
      </div>

      {isAdmin && (
        <>
          <VademecumModal
            isOpen={isVademecumModalOpen}
            onClose={() => { setIsVademecumModalOpen(false); setEditingVademecumItem(undefined); }}
            onSubmit={handleVademecumSubmit}
            initialData={editingVademecumItem}
            categories={vademecumCategories}
            defaultCategoryId={activeTab.type === 'vademecum' ? activeTab.categoryId : undefined}
          />
          <CategoryModal
            isOpen={isCategoryModalOpen}
            onClose={() => setIsCategoryModalOpen(false)}
            onSubmit={handleSaveCategories}
            initialCategories={vademecumCategories}
          />
        </>
      )}
    </div>
  );
};

interface BoardContentProps {
  posts: BoardPost[];
  loading: boolean;
  submitting: boolean;
  newPostContent: string;
  setNewPostContent: (v: string) => void;
  editingPost: BoardPost | null;
  editContent: string;
  setEditContent: (v: string) => void;
  canPost: boolean;
  selectedCityLabel: string;
  userId?: string;
  onSubmitPost: (e: React.FormEvent) => void;
  onEditPost: (post: BoardPost) => void;
  onUpdatePost: () => void;
  onDeletePost: (id: string) => void;
  onCancelEdit: () => void;
  formatDate: (d: string) => string;
}

const BoardContent: React.FC<BoardContentProps> = ({
  posts, loading, submitting, newPostContent, setNewPostContent,
  editingPost, editContent, setEditContent, canPost, selectedCityLabel,
  userId, onSubmitPost, onEditPost, onUpdatePost, onDeletePost, onCancelEdit, formatDate
}) => {
  return (
    <>
      {canPost && (
        <form onSubmit={onSubmitPost} className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <textarea
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            placeholder={`Scrivi qualcosa sulla bacheca ${selectedCityLabel}...`}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
            rows={4}
            disabled={submitting}
          />
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={submitting || !newPostContent.trim()}
              className="flex items-center gap-2 bg-rose-500 text-white px-6 py-2 rounded-lg hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Pubblicazione...' : 'Pubblica'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Caricamento post...</p>
          </div>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun post ancora</h3>
          <p className="text-gray-600">
            {canPost
              ? 'Sii il primo a condividere qualcosa sulla bacheca!'
              : 'Non ci sono ancora post su questa bacheca.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <div key={post.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {editingPost?.id === post.id ? (
                <div>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none mb-4"
                    rows={4}
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={onCancelEdit}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={onUpdatePost}
                      disabled={submitting || !editContent.trim()}
                      className="flex items-center gap-2 bg-rose-500 text-white px-4 py-2 rounded-lg hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Salva
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{post.author?.name || 'Utente sconosciuto'}</h3>
                      <p className="text-sm text-gray-500">{formatDate(post.created_at)}</p>
                    </div>
                    {post.author_id === userId && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => onEditPost(post)}
                          className="p-2 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Modifica"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDeletePost(post.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

interface VademecumContentProps {
  items: VademecumItem[];
  loading: boolean;
  isAdmin: boolean;
  onEdit: (item: VademecumItem) => void;
  onDelete: (id: string) => void;
}

const VademecumContent: React.FC<VademecumContentProps> = ({ items, loading, isAdmin, onEdit, onDelete }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
        <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessuna voce</h3>
        <p className="text-gray-600">Non ci sono ancora voci in questa categoria.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {items.map(item => (
        <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-50 rounded-lg">
                <Book className="h-5 w-5 text-rose-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">{item.title}</h2>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <button
                  onClick={() => onEdit(item)}
                  className="text-blue-600 hover:text-blue-700 p-1 hover:bg-blue-50 rounded"
                  title="Modifica"
                >
                  <Pencil className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onDelete(item.id)}
                  className="text-rose-600 hover:text-rose-700 p-1 hover:bg-rose-50 rounded"
                  title="Elimina"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
          <div className="prose max-w-none">
            {item.content.split('\n').map((paragraph, index) => (
              <p key={index} className="text-gray-600">{paragraph}</p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

interface VademecumModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; content: string; category_id: string }) => void;
  initialData?: VademecumItem;
  categories: VademecumCategory[];
  defaultCategoryId?: string;
}

const VademecumModal: React.FC<VademecumModalProps> = ({
  isOpen, onClose, onSubmit, initialData, categories, defaultCategoryId
}) => {
  const [formData, setFormData] = useState({ title: '', content: '', category_id: '' });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        content: initialData.content,
        category_id: initialData.category_id
      });
    } else {
      setFormData({
        title: '',
        content: '',
        category_id: defaultCategoryId || categories[0]?.id || ''
      });
    }
  }, [initialData, categories, defaultCategoryId, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Modifica Voce' : 'Nuova Voce'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Categoria</label>
            <select
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Titolo</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Contenuto</label>
            <textarea
              required
              rows={10}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700"
            >
              {initialData ? 'Salva Modifiche' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (categories: VademecumCategory[]) => void;
  initialCategories: VademecumCategory[];
}

const CategoryModal: React.FC<CategoryModalProps> = ({ isOpen, onClose, onSubmit, initialCategories }) => {
  const [categories, setCategories] = useState<VademecumCategory[]>([]);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    setCategories(initialCategories);
  }, [initialCategories]);

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    setCategories([
      ...categories,
      {
        id: crypto.randomUUID(),
        name: newCategory.trim(),
        order: categories.length + 1
      }
    ]);
    setNewCategory('');
  };

  const handleRemoveCategory = (id: string) => {
    setCategories(categories.filter(cat => cat.id !== id));
  };

  const handleMoveCategory = (id: string, direction: 'up' | 'down') => {
    const index = categories.findIndex(cat => cat.id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === categories.length - 1)
    ) return;

    const newCategories = [...categories];
    const temp = newCategories[index];
    if (direction === 'up') {
      newCategories[index] = newCategories[index - 1];
      newCategories[index - 1] = temp;
    } else {
      newCategories[index] = newCategories[index + 1];
      newCategories[index + 1] = temp;
    }
    setCategories(newCategories.map((cat, i) => ({ ...cat, order: i + 1 })));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Gestione Categorie</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
              placeholder="Nuova categoria..."
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
            />
            <button
              onClick={handleAddCategory}
              className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700"
            >
              Aggiungi
            </button>
          </div>
        </div>

        <div className="space-y-2 mb-6">
          {categories.map((category, index) => (
            <div
              key={category.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <span>{category.name}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleMoveCategory(category.id, 'up')}
                  disabled={index === 0}
                  className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  ↑
                </button>
                <button
                  onClick={() => handleMoveCategory(category.id, 'down')}
                  disabled={index === categories.length - 1}
                  className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  ↓
                </button>
                <button
                  onClick={() => handleRemoveCategory(category.id)}
                  className="p-1 text-rose-600 hover:text-rose-900"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Annulla
          </button>
          <button
            onClick={() => onSubmit(categories)}
            className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700"
          >
            Salva
          </button>
        </div>
      </div>
    </div>
  );
};

export default Board;
